import React, { useState } from "react";
import { URLS } from "../constants/urls";
import "../styles/DeleteAccount.css";

const isAppleRelay = (e) => e.trim().toLowerCase().endsWith("@privaterelay.appleid.com");

function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Apple relay verification popup state
  const [appleModalOpen, setAppleModalOpen] = useState(false);
  const [altEmail, setAltEmail] = useState("");
  const [birthday, setBirthday] = useState("");
  const [altEmailError, setAltEmailError] = useState("");
  const [birthdayError, setBirthdayError] = useState("");
  const [isAppleVerifying, setIsAppleVerifying] = useState(false);

  /* ── Validation ── */
  const validateEmail = () => {
    if (!email.trim()) { setEmailError("Please enter your email address."); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailError("Please enter a valid email address."); return false; }
    setEmailError(""); return true;
  };

  const validateAppleFields = () => {
    let valid = true;
    if (!altEmail.trim()) { setAltEmailError("Please enter an alternative email."); valid = false; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(altEmail)) { setAltEmailError("Please enter a valid email address."); valid = false; }
    else setAltEmailError("");
    if (!birthday.trim()) { setBirthdayError("Please enter your date of birth."); valid = false; }
    else setBirthdayError("");
    return valid;
  };

  /* ── API helpers ── */
  const checkEmail = async (emailToCheck, extra = {}) => {
    const response = await fetch(`${URLS.API_URL}/check-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailToCheck, ...extra }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data?.message || "No account found with this email address.");
    return data.data.userId;
  };

  const requestDeletion = async (userId, extra = {}) => {
    const response = await fetch(`${URLS.API_URL}/${userId}/request-deletion`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: "User requested deletion via web", entredGames: 0, ...extra }),
    });
    const data = await response.json(); 
    if (!response.ok) throw new Error(data?.message || "Failed to send deletion request.");
  };

  /* ── Open delete confirmation ── */
  const openModal = () => {
    if (!validateEmail()) return;
    if (isAppleRelay(email)) { setAppleModalOpen(true); return; }
    setApiError(""); setSuccessMsg(""); setModalOpen(true);
  };

  const closeModal = () => { if (!isDeleting) { setModalOpen(false); setApiError(""); } };

  /* ── Confirm delete (standard) ── */
  const handleConfirmDelete = async () => {
    setIsDeleting(true); setApiError("");
    try {
      const userId = await checkEmail(email);
      await requestDeletion(userId);
      setModalOpen(false);
      setSuccessMsg(`A confirmation email has been sent to ${email}. Please check your inbox to complete account deletion.`);
    } catch (err) {
      setApiError(err.message || "Something went wrong. Please try again.");
    } finally { setIsDeleting(false); }
  };

  /* ── Apple relay: verify then delete ── */
  const handleAppleVerify = async () => {
    if (!validateAppleFields()) return;
    setIsAppleVerifying(true); setApiError("");
    try {
      const userId = await checkEmail(altEmail, { relayEmail: email, birthday });
      await requestDeletion(userId, { relayEmail: email, newEmail: altEmail });
      setAppleModalOpen(false);
      setSuccessMsg(`A confirmation email has been sent to ${altEmail}. Please check your inbox to complete account deletion.`);
    } catch (err) {
      setApiError(err.message || "Something went wrong. Please try again.");
    } finally { setIsAppleVerifying(false); }
  };

  const closeAppleModal = () => {
    if (!isAppleVerifying) {
      setAppleModalOpen(false);
      setAltEmail(""); setBirthday("");
      setAltEmailError(""); setBirthdayError("");
      setApiError("");
    }
  };

  React.useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") { closeModal(); closeAppleModal(); } };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  });

  return (
    <>
      <div className="delete-account-page">
        <div className="delete-account-card">
          <h1>Delete your account</h1>
          <p className="delete-account-description">
            Deleting your Fun Comp account is permanent and cannot be undone. All virtual currency and gifts will be forfeited. Your personal data, and profile information will be removed from our database.
          </p>

          <div className="delete-account-divider" />

          {successMsg && <div className="da-success-msg">{successMsg}</div>}
          {apiError && !modalOpen && !appleModalOpen && <div className="da-api-error">{apiError}</div>}

          <div className="da-email-wrapper">
            <label className="da-email-label" htmlFor="da-email">Email address</label>
            <input
              id="da-email"
              className={`da-email-input ${emailError ? "da-email-input--error" : ""}`}
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError("");
                if (apiError) setApiError("");
                if (successMsg) setSuccessMsg("");
              }}
            />
            {emailError && <span className="da-email-error">{emailError}</span>}
          </div>

          <button className="btn-confirm-delete btn-confirm-delete--standalone" onClick={openModal}>
            Delete my account
          </button>
        </div>
      </div>

      {/* ── Standard delete confirmation modal ── */}
      <div
        className={`da-modal-overlay ${modalOpen ? "open" : ""}`}
        onClick={(e) => { if (e.target.classList.contains("da-modal-overlay")) closeModal(); }}
        role="dialog" aria-modal="true" aria-labelledby="da-modal-title"
      >
        <div className="da-modal">
          <div className="da-modal-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                stroke="#fc3c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 id="da-modal-title">Are you sure you want to<br />delete your account?</h2>
          <p>
            A confirmation email will be sent to <strong>{email}</strong>.
            Follow the link in that email to permanently delete your account and all associated data.
          </p>
          {apiError && modalOpen && <div className="da-api-error">{apiError}</div>}
          <div className="da-modal-actions">
            <button className="btn-confirm-delete" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? "Sending email…" : "Yes, send confirmation email"}
            </button>
            <button className="btn-cancel-delete" onClick={closeModal}>Cancel — keep my account</button>
          </div>
        </div>
      </div>

      {/* ── Apple private relay modal ── */}
      <div
        className={`da-modal-overlay ${appleModalOpen ? "open" : ""}`}
        onClick={(e) => { if (e.target.classList.contains("da-modal-overlay")) closeAppleModal(); }}
        role="dialog" aria-modal="true" aria-labelledby="apple-modal-title"
      >
        <div className="da-modal">
          <div className="da-modal-icon da-modal-icon--apple">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 5v6m0 4h.01"
                stroke="#3f8dfd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 id="apple-modal-title">Verify your identity</h2>
          <p>
            Your Apple private relay email can't be used to look up your account.
            Please provide your <strong>date of birth</strong> and the <strong>real email</strong> linked to your account.
          </p>

          <div className="da-apple-fields">
            <div className="da-email-wrapper">
              <label className="da-email-label" htmlFor="apple-alt-email">Account email address</label>
              <input
                id="apple-alt-email"
                className={`da-email-input ${altEmailError ? "da-email-input--error" : ""}`}
                type="email"
                placeholder="yourname@example.com"
                value={altEmail}
                onChange={(e) => { setAltEmail(e.target.value); if (altEmailError) setAltEmailError(""); }}
              />
              {altEmailError && <span className="da-email-error">{altEmailError}</span>}
            </div>

            <div className="da-email-wrapper">
              <label className="da-email-label" htmlFor="apple-birthday">Date of birth</label>
              <input
                id="apple-birthday"
                className={`da-email-input ${birthdayError ? "da-email-input--error" : ""}`}
                type="date"
                value={birthday}
                onChange={(e) => { setBirthday(e.target.value); if (birthdayError) setBirthdayError(""); }}
              />
              {birthdayError && <span className="da-email-error">{birthdayError}</span>}
            </div>
          </div>

          {apiError && appleModalOpen && <div className="da-api-error">{apiError}</div>}

          <div className="da-modal-actions">
            <button className="btn-confirm-delete" onClick={handleAppleVerify} disabled={isAppleVerifying}>
              {isAppleVerifying ? "Verifying…" : "Yes, send confirmation email"}
            </button>
            <button className="btn-cancel-delete" onClick={closeAppleModal}>Cancel</button>
          </div>
        </div>
      </div>
    </>
  );
}

export default DeleteAccount;